export const ADMIN_DASHBOARD_FALLBACK = "/dashboard";

export function isAdminAuthPath(pathname: string): boolean {
  return pathname === "/login";
}

export function isProtectedAdminPath(pathname: string): boolean {
  return pathname.startsWith("/dashboard");
}

export function sanitizeInternalRedirect(
  target: string | null | undefined,
  fallback: string = ADMIN_DASHBOARD_FALLBACK
): string {
  if (!target || !target.startsWith("/") || target.startsWith("//")) {
    return fallback;
  }

  try {
    const url = new URL(target, "http://localhost");
    if (isAdminAuthPath(url.pathname)) {
      return fallback;
    }
  } catch {
    return fallback;
  }

  return target;
}

export function buildAdminLoginRedirectPath(pathname: string, search: string = ""): string {
  const returnTo = `${pathname}${search}`;

  if (!returnTo || isAdminAuthPath(pathname)) {
    return "/login";
  }

  const params = new URLSearchParams({ redirect: returnTo });
  return `/login?${params.toString()}`;
}

interface ResolveAdminAuthRedirectInput {
  pathname: string;
  search?: string;
  hasSession: boolean;
  enforceServerGuard: boolean;
  redirectTarget?: string | null;
}

export function resolveAdminAuthRedirect({
  pathname,
  search = "",
  hasSession,
  enforceServerGuard,
  redirectTarget,
}: ResolveAdminAuthRedirectInput): string | null {
  if (!enforceServerGuard) {
    return null;
  }

  if (isProtectedAdminPath(pathname) && !hasSession) {
    return buildAdminLoginRedirectPath(pathname, search);
  }

  if (isAdminAuthPath(pathname) && !hasSession && redirectTarget) {
    const normalizedRedirect = sanitizeInternalRedirect(redirectTarget, "");
    if (!normalizedRedirect) {
      return pathname;
    }
  }

  if (isAdminAuthPath(pathname) && hasSession) {
    return sanitizeInternalRedirect(redirectTarget, ADMIN_DASHBOARD_FALLBACK);
  }

  return null;
}
